import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Upload, Settings, Cpu, Eraser, Wand2, Crop, ArrowRight, RefreshCcw, Undo, Trash2, Type, FileImage, Bold, Italic, Triangle, ImagePlus, Sun } from 'lucide-react';
import './App.css';

function App() {
  const [step, setStep] = useState(1); 
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('Idle');
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [appMode, setAppMode] = useState('vector'); 
  
  const [thickness, setThickness] = useState(4.0);
  const [baseThickness, setBaseThickness] = useState(2.0);
  const [numColors, setNumColors] = useState(4);
  const [baseShape, setBaseShape] = useState('contour');
  const [baseMargin, setBaseMargin] = useState(0);
  const [caulkSize, setCaulkSize] = useState(10);
  const [colorGap, setColorGap] = useState(0);
  const [sealStyle, setSealStyle] = useState('smooth');

  // Pedestal & Topper Controls
  const [includePedestal, setIncludePedestal] = useState(false);
  const [pedestalTolerance, setPedestalTolerance] = useState(0.4);
  const [slotLengthPct, setSlotLengthPct] = useState(80); // NEW: Slot Length State
  const [cakeTopper, setCakeTopper] = useState(false); 

  // Lithophane Controls
  const [isCurvedLitho, setIsCurvedLitho] = useState(false);
  const [includeLithoBase, setIncludeLithoBase] = useState(false);
  const [lithoSize, setLithoSize] = useState('auto');
  const [includeBoxFrame, setIncludeBoxFrame] = useState(false); 

  const [activeTool, setActiveTool] = useState('eraser'); 
  const [isDrawing, setIsDrawing] = useState(false);
  const [wandTolerance, setWandTolerance] = useState(30);
  const [hasEdited, setHasEdited] = useState(false); 

  const [textValue, setTextValue] = useState('VectorPrint');
  const [textFont, setTextFont] = useState('Inter');
  const [textSize, setTextSize] = useState(120);
  const [textBold, setTextBold] = useState(true);
  const [textItalic, setTextItalic] = useState(false);
  const [textColor, setTextColor] = useState('#000000');
  const [textOutlineColor, setTextOutlineColor] = useState('#38bdf8');
  const [textOutlineWidth, setTextOutlineWidth] = useState(0);

  const [shapeType, setShapeType] = useState('star');
  const [shapeSize, setShapeSize] = useState(150);
  const [shapeColor, setShapeColor] = useState('#facc15');
  const [shapeOutlineColor, setShapeOutlineColor] = useState('#000000');
  const [shapeOutlineWidth, setShapeOutlineWidth] = useState(0);

  const [stampImage, setStampImage] = useState(null);
  const [stampSize, setStampSize] = useState(200);

  const [cropStart, setCropStart] = useState(null);
  const [cropCurrent, setCropCurrent] = useState(null);
  const [cropStartScreen, setCropStartScreen] = useState(null);
  const [cropCurrentScreen, setCropCurrentScreen] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  
  const [history, setHistory] = useState([]);

  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null); 
  const contextRef = useRef(null);
  const originalImageRef = useRef(null);

  const handleGlobalDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleGlobalDrop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setHasEdited(false);
      loadImageOntoCanvas(selectedFile);
    }
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const selectedFile = e.dataTransfer.files[0];
      setFile(selectedFile);
      setHasEdited(false);
      loadImageOntoCanvas(selectedFile);
    }
  };

  const startBlankCanvas = () => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 1200;
    tempCanvas.height = 800;
    const ctx = tempCanvas.getContext('2d');
    ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

    tempCanvas.toBlob((blob) => {
      const blankFile = new File([blob], "blank_sign.png", { type: 'image/png' });
      setFile(blankFile);
      setHasEdited(true); 
      loadImageOntoCanvas(blankFile);
      setActiveTool('text');
      setStatus('Transparent canvas ready. Use the Laser alignment guide to stamp text, shapes, or objects!');
    }, 'image/png');
  };

  const handleStampLoad = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          setStampImage(img);
          setStatus('Object loaded! Click the canvas to stamp it.');
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const syncCanvases = (width, height) => {
    const canvas = canvasRef.current;
    const overlay = overlayCanvasRef.current;
    if (canvas && overlay) {
      canvas.width = width;
      canvas.height = height;
      overlay.width = width;
      overlay.height = height;
    }
  };

  const loadImageOntoCanvas = (selectedFile) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        syncCanvases(img.width, img.height);
        const ctx = canvasRef.current.getContext('2d');
        ctx.drawImage(img, 0, 0, img.width, img.height);
        
        contextRef.current = ctx;
        originalImageRef.current = img;
        setHistory([]); 
        setStatus('Ready for processing.');
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(selectedFile);
  };

  useEffect(() => {
    if (activeTool !== 'text' && activeTool !== 'shape' && activeTool !== 'stamp' && overlayCanvasRef.current) {
      const overlay = overlayCanvasRef.current;
      const octx = overlay.getContext('2d');
      octx.clearRect(0, 0, overlay.width, overlay.height);
    }
  }, [activeTool]);

  const saveState = () => {
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL();
      setHistory(prev => [...prev, dataUrl]);
    }
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const previousState = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    
    const img = new Image();
    img.onload = () => {
      syncCanvases(img.width, img.height);
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, img.width, img.height);
      ctx.drawImage(img, 0, 0);
      setStatus('Last action undone.');
    };
    img.src = previousState;
  };

  const resetCanvas = () => {
    if (originalImageRef.current && canvasRef.current) {
      const img = originalImageRef.current;
      syncCanvases(img.width, img.height);
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, img.width, img.height);
      ctx.drawImage(img, 0, 0, img.width, img.height);
      
      setHasEdited(false);
      setHistory([]); 
      setStatus('Canvas reset to original. Bypass active.');
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setHistory([]);
    setHasEdited(false);
    setStep(1);
    setStatus('Idle');
    if (document.getElementById('fileInput')) document.getElementById('fileInput').value = ''; 
    if (document.getElementById('stampInput')) document.getElementById('stampInput').value = ''; 
  };

  const floodFill = (ctx, startX, startY, tolerance) => {
    const canvas = ctx.canvas;
    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    const startPos = (startY * width + startX) * 4;
    const startR = data[startPos];
    const startG = data[startPos + 1];
    const startB = data[startPos + 2];
    const startA = data[startPos + 3];

    if (startA === 0) return; 

    const matchColor = (pos) => {
      const r = data[pos];
      const g = data[pos + 1];
      const b = data[pos + 2];
      const a = data[pos + 3];
      const dist = Math.sqrt(Math.pow(r - startR, 2) + Math.pow(g - startG, 2) + Math.pow(b - startB, 2));
      return dist <= tolerance && a > 0;
    };

    const pixelStack = [[startX, startY]];

    while (pixelStack.length > 0) {
      const newPos = pixelStack.pop();
      const x = newPos[0];
      let y = newPos[1];
      let pixelPos = (y * width + x) * 4;
      
      while (y-- >= 0 && matchColor(pixelPos)) { pixelPos -= width * 4; }
      pixelPos += width * 4;
      ++y;

      let reachLeft = false;
      let reachRight = false;

      while (y++ < height - 1 && matchColor(pixelPos)) {
        data[pixelPos] = 0; data[pixelPos + 1] = 0; data[pixelPos + 2] = 0; data[pixelPos + 3] = 0;
        if (x > 0) {
          if (matchColor(pixelPos - 4)) {
            if (!reachLeft) { pixelStack.push([x - 1, y]); reachLeft = true; }
          } else if (reachLeft) { reachLeft = false; }
        }
        if (x < width - 1) {
          if (matchColor(pixelPos + 4)) {
            if (!reachRight) { pixelStack.push([x + 1, y]); reachRight = true; }
          } else if (reachRight) { reachRight = false; }
        }
        pixelPos += width * 4;
      }
    }
    ctx.putImageData(imageData, 0, 0);
    setHasEdited(true); 
  };

  const getMappedCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      intrinsicX: Math.floor((e.nativeEvent.clientX - rect.left) * scaleX),
      intrinsicY: Math.floor((e.nativeEvent.clientY - rect.top) * scaleY),
      screenX: e.nativeEvent.clientX - rect.left,
      screenY: e.nativeEvent.clientY - rect.top
    };
  };

  const drawShapeOnCanvas = (ctx, type, x, y, size, fill, outlineW, outlineC) => {
    ctx.fillStyle = fill;
    if (outlineW > 0) {
      ctx.lineWidth = outlineW;
      ctx.strokeStyle = outlineC;
    }
    ctx.beginPath();
    
    const radius = size / 2;
    
    if (type === 'star') {
      const spikes = 5;
      const innerRadius = size / 4;
      let rot = Math.PI / 2 * 3;
      let step = Math.PI / spikes;
      ctx.moveTo(x, y - radius);
      for (let i = 0; i < spikes; i++) {
        ctx.lineTo(x + Math.cos(rot) * radius, y + Math.sin(rot) * radius);
        rot += step;
        ctx.lineTo(x + Math.cos(rot) * innerRadius, y + Math.sin(rot) * innerRadius);
        rot += step;
      }
      ctx.closePath();
    } else if (type === 'circle') {
      ctx.arc(x, y, radius, 0, Math.PI * 2);
    } else if (type === 'square') {
      ctx.rect(x - radius, y - radius, size, size);
    } else if (type === 'triangle') {
      ctx.moveTo(x, y - radius);
      ctx.lineTo(x + radius, y + radius);
      ctx.lineTo(x - radius, y + radius);
      ctx.closePath();
    } else if (['pentagon', 'hexagon', 'octagon'].includes(type)) {
      const sides = type === 'pentagon' ? 5 : type === 'hexagon' ? 6 : 8;
      let rot = -Math.PI / 2; 
      let step = (Math.PI * 2) / sides;
      ctx.moveTo(x + Math.cos(rot) * radius, y + Math.sin(rot) * radius);
      for (let i = 1; i <= sides; i++) {
        ctx.lineTo(x + Math.cos(rot + step * i) * radius, y + Math.sin(rot + step * i) * radius);
      }
      ctx.closePath();
    } else if (type === 'heart') {
      const topCurveHeight = size * 0.3;
      ctx.moveTo(x, y - topCurveHeight);
      ctx.bezierCurveTo(x, y - radius, x - radius, y - radius, x - radius, y - topCurveHeight);
      ctx.bezierCurveTo(x - radius, y + size / 4, x, y + size / 4, x, y + radius);
      ctx.bezierCurveTo(x, y + size / 4, x + radius, y + size / 4, x + radius, y - topCurveHeight);
      ctx.bezierCurveTo(x + radius, y - radius, x, y - radius, x, y - topCurveHeight);
      ctx.closePath();
    }
    
    if (outlineW > 0) ctx.stroke();
    ctx.fill();
  };

  const handleMouseDown = (e) => {
    const { intrinsicX, intrinsicY, screenX, screenY } = getMappedCoords(e);

    if (activeTool === 'eraser') {
      saveState();
      contextRef.current.beginPath();
      contextRef.current.moveTo(intrinsicX, intrinsicY);
      setIsDrawing(true);
    } else if (activeTool === 'wand') {
      saveState(); 
      floodFill(contextRef.current, intrinsicX, intrinsicY, wandTolerance);
      setStatus('Magic Wand applied.');
    } else if (activeTool === 'crop') {
      setCropStart({ x: intrinsicX, y: intrinsicY });
      setCropCurrent({ x: intrinsicX, y: intrinsicY });
      setCropStartScreen({ x: screenX, y: screenY });
      setCropCurrentScreen({ x: screenX, y: screenY });
      setIsCropping(true);
    } else if (activeTool === 'text') {
      saveState();
      const ctx = contextRef.current;
      const fontStr = `${textItalic ? 'italic ' : ''}${textBold ? 'bold ' : ''}${textSize}px ${textFont}, sans-serif`;
      ctx.font = fontStr;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      if (textOutlineWidth > 0) {
        ctx.lineWidth = textOutlineWidth;
        ctx.strokeStyle = textOutlineColor;
        ctx.strokeText(textValue, intrinsicX, intrinsicY);
      }
      ctx.fillStyle = textColor;
      ctx.fillText(textValue, intrinsicX, intrinsicY);
      
      setHasEdited(true);
      setStatus(`Stamped text: "${textValue}"`);
    } else if (activeTool === 'shape') {
      saveState();
      const ctx = contextRef.current;
      drawShapeOnCanvas(ctx, shapeType, intrinsicX, intrinsicY, shapeSize, shapeColor, shapeOutlineWidth, shapeOutlineColor);
      setHasEdited(true);
      setStatus(`Stamped ${shapeType} shape.`);
    } else if (activeTool === 'stamp' && stampImage) {
      saveState();
      const ctx = contextRef.current;
      const aspect = stampImage.width / stampImage.height;
      const drawW = parseInt(stampSize);
      const drawH = drawW / aspect;
      ctx.drawImage(stampImage, intrinsicX - drawW / 2, intrinsicY - drawH / 2, drawW, drawH);
      setHasEdited(true);
      setStatus('Object stamped successfully!');
    }
  };

  const handleMouseMove = (e) => {
    const { intrinsicX, intrinsicY, screenX, screenY } = getMappedCoords(e);

    if (activeTool === 'eraser' && isDrawing) {
      contextRef.current.globalCompositeOperation = 'destination-out';
      contextRef.current.lineTo(intrinsicX, intrinsicY);
      contextRef.current.stroke();
      contextRef.current.lineWidth = Math.max(10, canvasRef.current.width * 0.02); 
      contextRef.current.lineCap = 'round';
      setHasEdited(true); 
    } else if (activeTool === 'crop' && isCropping) {
      setCropCurrent({ x: intrinsicX, y: intrinsicY });
      setCropCurrentScreen({ x: screenX, y: screenY });
    } else if (activeTool === 'text' || activeTool === 'shape' || activeTool === 'stamp') {
      const overlay = overlayCanvasRef.current;
      const octx = overlay.getContext('2d');
      octx.clearRect(0, 0, overlay.width, overlay.height);

      octx.beginPath();
      octx.moveTo(0, intrinsicY);
      octx.lineTo(overlay.width, intrinsicY);
      octx.setLineDash([15, 15]);
      octx.strokeStyle = 'rgba(250, 204, 21, 0.6)'; 
      octx.lineWidth = Math.max(2, overlay.width * 0.005);
      octx.stroke();
      octx.setLineDash([]);

      octx.globalAlpha = 0.5; 
      
      if (activeTool === 'text') {
        const fontStr = `${textItalic ? 'italic ' : ''}${textBold ? 'bold ' : ''}${textSize}px ${textFont}, sans-serif`;
        octx.font = fontStr;
        octx.textAlign = 'center';
        octx.textBaseline = 'middle';

        if (textOutlineWidth > 0) {
            octx.lineWidth = textOutlineWidth;
            octx.strokeStyle = textOutlineColor;
            octx.strokeText(textValue, intrinsicX, intrinsicY);
        }
        octx.fillStyle = textColor;
        octx.fillText(textValue, intrinsicX, intrinsicY);
      } else if (activeTool === 'shape') {
        drawShapeOnCanvas(octx, shapeType, intrinsicX, intrinsicY, shapeSize, shapeColor, shapeOutlineWidth, shapeOutlineColor);
      } else if (activeTool === 'stamp' && stampImage) {
        const aspect = stampImage.width / stampImage.height;
        const drawW = parseInt(stampSize);
        const drawH = drawW / aspect;
        octx.drawImage(stampImage, intrinsicX - drawW / 2, intrinsicY - drawH / 2, drawW, drawH);
      }
      
      octx.globalAlpha = 1.0;
    }
  };

  const handleMouseOut = () => {
    if (overlayCanvasRef.current) {
      const overlay = overlayCanvasRef.current;
      const octx = overlay.getContext('2d');
      octx.clearRect(0, 0, overlay.width, overlay.height);
    }
  };

  const handleMouseUp = () => {
    if (activeTool === 'eraser') {
      contextRef.current.closePath();
      setIsDrawing(false);
      contextRef.current.globalCompositeOperation = 'source-over'; 
    } else if (activeTool === 'crop' && isCropping) {
      setIsCropping(false);
      
      if (cropStart && cropCurrent) {
        const startX = Math.min(cropStart.x, cropCurrent.x);
        const startY = Math.min(cropStart.y, cropCurrent.y);
        const width = Math.abs(cropCurrent.x - cropStart.x);
        const height = Math.abs(cropCurrent.y - cropStart.y);

        if (width > 15 && height > 15) {
          saveState(); 
          const ctx = contextRef.current;
          const croppedImageData = ctx.getImageData(startX, startY, width, height);
          
          syncCanvases(width, height);
          ctx.putImageData(croppedImageData, 0, 0);
          
          setHasEdited(true);
          setStatus('Image cropped perfectly.');
        }
        setCropStart(null);
        setCropCurrent(null);
        setCropStartScreen(null);
        setCropCurrentScreen(null);
      }
    }
  };

  const sendToEngine = async () => {
    setLoading(true);

    const processUpload = async (fileToUpload) => {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('mode', appMode);
      
      if (appMode === 'lithophane') {
        formData.append('is_curved', isCurvedLitho);
        formData.append('include_litho_base', includeLithoBase);
        formData.append('litho_size', lithoSize);
        formData.append('include_box_frame', includeBoxFrame);
      } else {
        formData.append('thickness', thickness);
        formData.append('base_thickness', baseThickness);
        formData.append('num_colors', numColors);
        formData.append('shape', baseShape); 
        formData.append('base_margin', baseMargin);
        formData.append('caulk_size', caulkSize);
        formData.append('color_gap', colorGap);
        formData.append('seal_style', sealStyle);
        formData.append('include_pedestal', includePedestal);
        formData.append('pedestal_tolerance', pedestalTolerance);
        formData.append('slot_length_pct', slotLengthPct); // NEW: Send to server
        formData.append('cake_topper', cakeTopper);
      }

      try {
        setStatus(appMode === 'lithophane' ? `Engine generating high-res Lithophane...` : `Engine generating 3D ${baseShape} plate...`);
        // We check if a live API URL is provided. If not, it defaults back to your Mac Mini's local port 5001!
        const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001';
        const response = await axios.post(`${apiUrl}/convert`, formData, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'CFS_3D_Print_Files.zip');
        document.body.appendChild(link);
        link.click();
        setStatus('Conversion Complete!');
      } catch (error) {
        console.error(error);
        setStatus('Engine failed to process the image.');
      } finally {
        setLoading(false);
      }
    };

    if (hasEdited) {
      setStatus('Packaging high-res edited image from canvas...');
      canvasRef.current.toBlob((blob) => {
        const editedFile = new File([blob], "studio_edited_logo.png", { type: 'image/png' });
        processUpload(editedFile);
      }, 'image/png');
    } else {
      setStatus('Sending original pristine file to engine...');
      processUpload(file);
    }
  };

  return (
    <div className="app-container" onDragOver={handleGlobalDragOver} onDrop={handleGlobalDrop}>
      <header className="header" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', padding: '20px 40px' }}>
        <div style={{ textAlign: 'left' }}>
          <h1 style={{ margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}><Cpu size={32} /> VectorPrint3D</h1>
          <p style={{ margin: '5px 0 0 0' }}>Pro-Grade Color Vector-to-STL Engine</p>
        </div>

        <div style={{ display: 'flex', gap: '10px', backgroundColor: '#0f172a', padding: '5px', borderRadius: '8px', border: '1px solid #334155' }}>
          <button 
            onClick={() => setAppMode('vector')}
            style={{ padding: '8px 15px', backgroundColor: appMode === 'vector' ? '#38bdf8' : 'transparent', color: appMode === 'vector' ? '#fff' : '#64748b', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Cpu size={16} /> Color Vector Mode
          </button>
          <button 
            onClick={() => setAppMode('lithophane')}
            style={{ padding: '8px 15px', backgroundColor: appMode === 'lithophane' ? '#facc15' : 'transparent', color: appMode === 'lithophane' ? '#000' : '#64748b', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Sun size={16} /> Lithophane Mode
          </button>
        </div>
      </header>

      <main className="main-content">
        
        <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', justifyContent: 'center' }}>
          <button 
            style={{ padding: '10px 20px', backgroundColor: step === 1 ? '#38bdf8' : '#333', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
            onClick={() => setStep(1)}
          >
            Step 1: Image Studio
          </button>
          <button 
            style={{ padding: '10px 20px', backgroundColor: step === 2 ? '#38bdf8' : '#333', color: 'white', border: 'none', borderRadius: '5px', cursor: file ? 'pointer' : 'not-allowed', opacity: file ? 1 : 0.5 }}
            onClick={() => { if (file) setStep(2); }}
          >
            Step 2: 3D Engine
          </button>
        </div>

        {/* STEP 1: THE STUDIO */}
        <div style={{ display: step === 1 ? 'flex' : 'none', flexDirection: 'column', alignItems: 'center', backgroundColor: '#1e293b', padding: '20px', borderRadius: '10px' }}>
          {!file ? (
             <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '600px', gap: '15px' }}>
               <div 
                 className="upload-zone" 
                 onDragOver={onDragOver}
                 onDragLeave={onDragLeave}
                 onDrop={onDrop}
                 onClick={() => document.getElementById('fileInput').click()} 
                 style={{ width: '100%', borderColor: isDragging ? '#38bdf8' : '' }}
               >
                 <Upload size={48} />
                 <input type="file" id="fileInput" className="file-input" accept=".jpg,.jpeg,.png,.svg" onChange={handleFileSelect} />
                 <div className="file-label">
                   {appMode === 'lithophane' ? 'Drop Photograph Here for Lithophane' : 'Drop Main Logo Here to Open Studio'}
                 </div>
               </div>

               {appMode === 'vector' && (
                 <>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                     <div style={{ flex: 1, height: '1px', backgroundColor: '#334155' }} />
                     <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: 'bold' }}>OR</span>
                     <div style={{ flex: 1, height: '1px', backgroundColor: '#334155' }} />
                   </div>
                   <button 
                     onClick={startBlankCanvas}
                     style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '15px', backgroundColor: '#0f172a', color: '#38bdf8', border: '2px solid #38bdf8', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
                     <FileImage size={24} /> Create Blank Canvas
                   </button>
                 </>
               )}
             </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxWidth: '600px', marginBottom: '15px' }}>
                <div style={{ display: 'flex', gap: '10px', backgroundColor: '#0f172a', padding: '10px', borderRadius: '8px', flexWrap: 'wrap' }}>
                  <button onClick={() => setActiveTool('crop')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', backgroundColor: activeTool === 'crop' ? '#38bdf8' : 'transparent', color: 'white', border: '1px solid #38bdf8', borderRadius: '5px', cursor: 'pointer' }}>
                    <Crop size={18} /> Crop
                  </button>
                  <button onClick={() => setActiveTool('eraser')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', backgroundColor: activeTool === 'eraser' ? '#38bdf8' : 'transparent', color: 'white', border: '1px solid #38bdf8', borderRadius: '5px', cursor: 'pointer' }}>
                    <Eraser size={18} /> Eraser
                  </button>
                  
                  {appMode === 'vector' && (
                    <>
                      <button onClick={() => setActiveTool('wand')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', backgroundColor: activeTool === 'wand' ? '#38bdf8' : 'transparent', color: 'white', border: '1px solid #38bdf8', borderRadius: '5px', cursor: 'pointer' }}>
                        <Wand2 size={18} /> Wand
                      </button>
                      <button onClick={() => setActiveTool('text')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', backgroundColor: activeTool === 'text' ? '#38bdf8' : 'transparent', color: 'white', border: '1px solid #38bdf8', borderRadius: '5px', cursor: 'pointer' }}>
                        <Type size={18} /> Text
                      </button>
                      <button onClick={() => setActiveTool('shape')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', backgroundColor: activeTool === 'shape' ? '#38bdf8' : 'transparent', color: 'white', border: '1px solid #38bdf8', borderRadius: '5px', cursor: 'pointer' }}>
                        <Triangle size={18} /> Shape
                      </button>
                      <button onClick={() => setActiveTool('stamp')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', backgroundColor: activeTool === 'stamp' ? '#38bdf8' : 'transparent', color: 'white', border: '1px solid #38bdf8', borderRadius: '5px', cursor: 'pointer' }}>
                        <ImagePlus size={18} /> Stamp
                      </button>
                    </>
                  )}

                  <button 
                    onClick={handleUndo} 
                    disabled={history.length === 0}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', backgroundColor: 'transparent', color: history.length === 0 ? '#64748b' : '#facc15', border: `1px solid ${history.length === 0 ? '#64748b' : '#facc15'}`, borderRadius: '5px', cursor: history.length === 0 ? 'not-allowed' : 'pointer', marginLeft: 'auto' }}
                  >
                    <Undo size={18} /> Undo
                  </button>
                  
                  <button onClick={resetCanvas} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '5px', cursor: 'pointer' }}>
                    <RefreshCcw size={18} /> Reset
                  </button>
                  <button onClick={handleClearFile} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', backgroundColor: '#7f1d1d', color: 'white', border: '1px solid #ef4444', borderRadius: '5px', cursor: 'pointer' }}>
                    <Trash2 size={18} /> Clear
                  </button>
                </div>

                {activeTool === 'wand' && appMode === 'vector' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', backgroundColor: '#1e293b', padding: '10px', borderRadius: '8px', border: '1px dashed #38bdf8' }}>
                    <span style={{ color: '#fff', fontSize: '14px' }}>Wand Tolerance: {wandTolerance}</span>
                    <input type="range" min="0" max="100" value={wandTolerance} onChange={(e) => setWandTolerance(e.target.value)} style={{ flex: 1 }}/>
                  </div>
                )}

                {activeTool === 'text' && appMode === 'vector' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px', border: '1px dashed #38bdf8' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input type="text" value={textValue} onChange={(e) => setTextValue(e.target.value)} placeholder="Type text here..." style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid #475569', backgroundColor: '#0f172a', color: 'white', fontSize: '16px' }}/>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <select value={textFont} onChange={(e) => setTextFont(e.target.value)} style={{ padding: '8px', borderRadius: '5px', backgroundColor: '#0f172a', color: 'white', border: '1px solid #475569', flex: 1 }}>
                        <option value="Inter">Inter (Clean)</option>
                        <option value="Arial">Arial (Standard)</option>
                        <option value="Verdana">Verdana (Wide)</option>
                        <option value="Tahoma">Tahoma (Modern)</option>
                        <option value="Georgia">Georgia (Serif)</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Garamond">Garamond (Elegant)</option>
                        <option value="Impact">Impact (Heavy/Poster)</option>
                        <option value="Arial Black">Arial Black (Massive)</option>
                        <option value="Courier New">Courier (Typewriter)</option>
                        <option value="Comic Sans MS">Comic Sans (Playful)</option>
                        <option value="Brush Script MT">Brush Script (Cursive)</option>
                      </select>
                      <button onClick={() => setTextBold(!textBold)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', backgroundColor: textBold ? '#38bdf8' : '#0f172a', color: 'white', border: '1px solid #475569', borderRadius: '5px', cursor: 'pointer' }}><Bold size={18} /></button>
                      <button onClick={() => setTextItalic(!textItalic)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', backgroundColor: textItalic ? '#38bdf8' : '#0f172a', color: 'white', border: '1px solid #475569', borderRadius: '5px', cursor: 'pointer' }}><Italic size={18} /></button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: '#0f172a', padding: '5px', borderRadius: '5px', border: '1px solid #475569' }}>
                        <label style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '5px' }}>Fill:</label>
                        <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}/>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ color: '#fff', fontSize: '14px', width: '70px' }}>Size: {textSize}</span>
                      <input type="range" min="20" max="400" value={textSize} onChange={(e) => setTextSize(e.target.value)} style={{ flex: 1 }}/>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ color: '#fff', fontSize: '14px', width: '70px' }}>Outline: {textOutlineWidth}</span>
                      <input type="range" min="0" max="50" value={textOutlineWidth} onChange={(e) => setTextOutlineWidth(e.target.value)} style={{ flex: 1 }}/>
                      <input type="color" value={textOutlineColor} onChange={(e) => setTextOutlineColor(e.target.value)} style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}/>
                    </div>
                  </div>
                )}

                {activeTool === 'shape' && appMode === 'vector' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px', border: '1px dashed #38bdf8' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <select value={shapeType} onChange={(e) => setShapeType(e.target.value)} style={{ padding: '8px', borderRadius: '5px', backgroundColor: '#0f172a', color: 'white', border: '1px solid #475569', flex: 1 }}>
                        <option value="star">Star</option>
                        <option value="circle">Circle</option>
                        <option value="square">Square</option>
                        <option value="triangle">Triangle</option>
                        <option value="pentagon">Pentagon</option>
                        <option value="hexagon">Hexagon</option>
                        <option value="octagon">Octagon</option>
                        <option value="heart">Heart</option>
                      </select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: '#0f172a', padding: '5px', borderRadius: '5px', border: '1px solid #475569' }}>
                        <label style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '5px' }}>Fill:</label>
                        <input type="color" value={shapeColor} onChange={(e) => setShapeColor(e.target.value)} style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}/>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ color: '#fff', fontSize: '14px', width: '70px' }}>Size: {shapeSize}</span>
                      <input type="range" min="20" max="600" value={shapeSize} onChange={(e) => setShapeSize(e.target.value)} style={{ flex: 1 }}/>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ color: '#fff', fontSize: '14px', width: '70px' }}>Outline: {shapeOutlineWidth}</span>
                      <input type="range" min="0" max="50" value={shapeOutlineWidth} onChange={(e) => setShapeOutlineWidth(e.target.value)} style={{ flex: 1 }}/>
                      <input type="color" value={shapeOutlineColor} onChange={(e) => setShapeOutlineColor(e.target.value)} style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}/>
                    </div>
                  </div>
                )}

                {activeTool === 'stamp' && appMode === 'vector' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px', border: '1px dashed #38bdf8' }}>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                      <button onClick={() => document.getElementById('stampInput').click()} style={{ padding: '10px 15px', backgroundColor: '#38bdf8', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>
                        Load Object/Icon
                      </button>
                      <input type="file" id="stampInput" style={{ display: 'none' }} accept=".png,.jpg,.jpeg,.svg" onChange={handleStampLoad} />
                      <span style={{ color: '#94a3b8', fontSize: '14px', fontStyle: 'italic' }}>
                        {stampImage ? 'Image ready to stamp!' : 'No object loaded yet.'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ color: '#fff', fontSize: '14px', width: '70px' }}>Size: {stampSize}</span>
                      <input type="range" min="20" max="1000" value={stampSize} onChange={(e) => setStampSize(e.target.value)} style={{ flex: 1 }} disabled={!stampImage}/>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ 
                position: 'relative', width: '100%', maxWidth: '600px',
                border: '2px dashed #475569', borderRadius: '5px', 
                cursor: activeTool === 'crop' || activeTool === 'eraser' ? 'crosshair' : (activeTool === 'text' || activeTool === 'shape' || activeTool === 'stamp' ? 'none' : 'cell'), 
                overflow: 'hidden', backgroundColor: '#e5e5e5',
                backgroundImage: 'linear-gradient(45deg, #cccccc 25%, transparent 25%), linear-gradient(-45deg, #cccccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #cccccc 75%), linear-gradient(-45deg, transparent 75%, #cccccc 75%)',
                backgroundSize: '20px 20px', backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
              }}>
                <canvas
                  ref={canvasRef}
                  style={{ display: 'block', backgroundColor: 'transparent', width: '100%', height: 'auto' }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseOut={handleMouseOut} 
                />
                <canvas
                  ref={overlayCanvasRef}
                  style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', width: '100%', height: '100%' }}
                />
                {isCropping && cropStartScreen && cropCurrentScreen && (
                  <div style={{
                    position: 'absolute',
                    left: Math.min(cropStartScreen.x, cropCurrentScreen.x),
                    top: Math.min(cropStartScreen.y, cropCurrentScreen.y),
                    width: Math.abs(cropCurrentScreen.x - cropStartScreen.x),
                    height: Math.abs(cropCurrentScreen.y - cropStartScreen.y),
                    border: '2px dashed #38bdf8',
                    backgroundColor: 'rgba(56, 189, 248, 0.2)',
                    pointerEvents: 'none'
                  }} />
                )}
              </div>

              <button className="convert-btn" onClick={() => setStep(2)} style={{ marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                Confirm & Proceed to Engine <ArrowRight size={20} />
              </button>
            </>
          )}
        </div>

        {/* STEP 2: THE ENGINE */}
        <div style={{ display: step === 2 ? 'flex' : 'none', flexDirection: 'column', gap: '20px' }}>
          
          {appMode === 'vector' ? (
            /* VECTOR MODE CONTROLS */
            <div className="controls-panel">
              <div className="control-group" style={{ borderBottom: '1px solid #333', paddingBottom: '15px', marginBottom: '10px' }}>
                <label style={{ color: '#38bdf8', fontWeight: 'bold' }}>Base Plate Shape</label>
                <select 
                  value={baseShape} 
                  onChange={(e) => setBaseShape(e.target.value)} 
                  style={{ width: '100%', padding: '10px', marginTop: '10px', borderRadius: '5px', backgroundColor: '#0f172a', color: 'white', border: '1px solid #475569', fontSize: '16px', cursor: 'pointer' }}
                >
                  <option value="contour">Contour (Tight Shrink-wrap)</option>
                  <option value="circle">Perfect Circle Badge</option>
                  <option value="rectangle">Bounding Rectangle Sign</option>
                </select>
              </div>

              <div style={{ backgroundColor: '#0f172a', border: '1px solid #475569', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: includePedestal ? '15px' : '0' }}>
                  <label style={{ color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="checkbox" checked={includePedestal} onChange={(e) => { setIncludePedestal(e.target.checked); if (e.target.checked) setCakeTopper(false); }} style={{ width: '18px', height: '18px', cursor: 'pointer' }}/>
                    Generate Display Pedestal (Standee Base)
                  </label>
                </div>
                {includePedestal && (
                  <>
                    <div className="control-group" style={{ margin: 0 }}>
                      <label style={{ color: '#94a3b8' }}>Slot Tolerance: {pedestalTolerance}mm</label>
                      <p style={{ fontSize: '11px', color: '#64748b', margin: '-5px 0 5px 0' }}>Increase if your printer suffers from 'elephants foot' so the standee fits perfectly.</p>
                      <input type="range" min="0.1" max="1.0" step="0.1" value={pedestalTolerance} onChange={(e) => setPedestalTolerance(e.target.value)} />
                    </div>
                    
                    <div className="control-group" style={{ margin: '15px 0 0 0' }}>
                      <label style={{ color: '#94a3b8' }}>Slot Span (Width): {slotLengthPct}%</label>
                      <p style={{ fontSize: '11px', color: '#64748b', margin: '-5px 0 5px 0' }}>Controls how far the slot stretches from left to right across the base.</p>
                      <input type="range" min="30" max="100" step="5" value={slotLengthPct} onChange={(e) => setSlotLengthPct(e.target.value)} />
                    </div>
                  </>
                )}

                <hr style={{ border: 'none', borderTop: '1px solid #334155', margin: '15px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="checkbox" checked={cakeTopper} onChange={(e) => { setCakeTopper(e.target.checked); if (e.target.checked) setIncludePedestal(false); }} style={{ width: '18px', height: '18px', cursor: 'pointer' }}/>
                    Generate Cake Topper Spike
                  </label>
                </div>
              </div>

              <div className="control-group">
                <label>Top Color Layer Height: {thickness}mm</label>
                <input type="range" min="0.5" max="10.0" step="0.5" value={thickness} onChange={(e) => setThickness(e.target.value)} />
              </div>
              <div className="control-group">
                <label>Base Plate Height: {baseThickness}mm</label>
                <input type="range" min="0.5" max="5.0" step="0.5" value={baseThickness} onChange={(e) => setBaseThickness(e.target.value)} />
              </div>
              <div className="control-group" style={{ borderBottom: '1px solid #333', paddingBottom: '15px', marginBottom: '10px' }}>
                <label>Max Color Count: {numColors}</label>
                <input type="range" min="2" max="8" step="1" value={numColors} onChange={(e) => setNumColors(e.target.value)} />
              </div>

              <div className="control-group">
                <label style={{ color: '#facc15' }}>Seal Style</label>
                <select value={sealStyle} onChange={(e) => setSealStyle(e.target.value)} style={{ width: '100%', padding: '8px', marginTop: '5px', borderRadius: '5px', backgroundColor: '#0f172a', color: 'white', border: '1px solid #475569', fontSize: '14px', cursor: 'pointer' }}>
                  <option value="smooth">Smooth (Organic Badges)</option>
                  <option value="sharp">Sharp (Gothic Text / Geometric)</option>
                </select>
              </div>

              <div className="control-group">
                <label style={{ color: '#facc15' }}>Base Plate Margin: {baseMargin}px</label>
                <input type="range" min="0" max="30" step="1" value={baseMargin} onChange={(e) => setBaseMargin(e.target.value)} />
              </div>
              <div className="control-group">
                <label style={{ color: '#facc15' }}>Perimeter Seal (Caulk): {caulkSize}px</label>
                <input type="range" min="0" max="50" step="1" value={caulkSize} onChange={(e) => setCaulkSize(e.target.value)} />
              </div>
              <div className="control-group">
                <label style={{ color: '#facc15' }}>Color Gap (Depth Channels): {colorGap}px</label>
                <input type="range" min="0" max="10" step="1" value={colorGap} onChange={(e) => setColorGap(e.target.value)} />
              </div>
            </div>
          ) : (
            /* LITHOPHANE MODE CONTROLS */
            <div className="controls-panel">
              <div style={{ backgroundColor: 'rgba(250, 204, 21, 0.1)', border: '1px solid #facc15', borderRadius: '8px', padding: '15px', display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '20px' }}>
                <Sun size={24} color="#facc15" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <h4 style={{ color: '#facc15', margin: '0 0 5px 0', fontSize: '14px', fontWeight: 'bold' }}>Lithophane Engine Active</h4>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0, lineHeight: '1.5' }}>
                    The engine will now read the brightness of every pixel in your photo and map it to a high-density 3D grid. Dark pixels will be thick (blocking light) and bright pixels will be thin (letting light through).
                  </p>
                </div>
              </div>

              <div style={{ backgroundColor: '#0f172a', border: '1px solid #475569', borderRadius: '8px', padding: '20px' }}>
                
                <div className="control-group" style={{ borderBottom: '1px solid #334155', paddingBottom: '15px', marginBottom: '15px' }}>
                  <label style={{ color: '#38bdf8', fontWeight: 'bold' }}>Lithophane Target Size</label>
                  <select 
                    value={lithoSize} 
                    onChange={(e) => setLithoSize(e.target.value)} 
                    style={{ width: '100%', padding: '10px', marginTop: '10px', borderRadius: '5px', backgroundColor: '#1e293b', color: 'white', border: '1px solid #475569', fontSize: '16px', cursor: 'pointer' }}
                  >
                    <option value="auto">Auto (Match Original Image Aspect Ratio)</option>
                    <option value="100x150">4x6 Standard Metric (100mm x 150mm)</option>
                    <option value="125x175">5x7 Standard Metric (125mm x 175mm)</option>
                    <option value="200x250">8x10 Standard Metric (200mm x 250mm)</option>
                  </select>
                  {lithoSize !== 'auto' && (
                    <p style={{ fontSize: '12px', color: '#facc15', marginTop: '8px', fontStyle: 'italic' }}>
                      Note: The engine will auto-crop your photo from the center to perfectly fit this exact ratio.
                    </p>
                  )}
                </div>

                <label style={{ color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '16px', marginBottom: '15px' }}>
                  <input type="checkbox" checked={isCurvedLitho} onChange={(e) => { setIsCurvedLitho(e.target.checked); if(e.target.checked) setIncludeBoxFrame(false); }} style={{ width: '20px', height: '20px', cursor: 'pointer' }}/>
                  Generate Curved Lithophane
                </label>
                
                <label style={{ color: isCurvedLitho ? '#64748b' : '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '16px', marginBottom: '15px' }}>
                  <input type="checkbox" checked={includeBoxFrame} disabled={isCurvedLitho} onChange={(e) => { setIncludeBoxFrame(e.target.checked); if(e.target.checked) setIncludeLithoBase(false); }} style={{ width: '20px', height: '20px', cursor: isCurvedLitho ? 'not-allowed' : 'pointer' }}/>
                  Generate Flat Light Box Frame
                </label>

                <label style={{ color: includeBoxFrame ? '#64748b' : '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '16px' }}>
                  <input type="checkbox" checked={includeLithoBase} disabled={includeBoxFrame} onChange={(e) => setIncludeLithoBase(e.target.checked)} style={{ width: '20px', height: '20px', cursor: includeBoxFrame ? 'not-allowed' : 'pointer' }}/>
                  Generate LED Puck Light Base
                </label>
              </div>
            </div>
          )}

          <button className="convert-btn" onClick={sendToEngine} disabled={loading} style={{ backgroundColor: appMode === 'lithophane' ? '#facc15' : '#38bdf8', color: appMode === 'lithophane' ? '#000' : '#fff' }}>
            {loading ? "Generating High-Res Model..." : "Generate 3D Print File"}
          </button>
        </div>

        <div className="status-bar"><p>Status: <strong>{status}</strong></p></div>
      </main>
      <footer className="footer"><Settings size={20} /> System Operational: Port 5001</footer>
    </div>
  );
}

export default App;
